import type { z } from 'zod';

import {
	applicationIdParamsSchema,
	applyRequestSchema,
	applyResponseSchema,
	botAccessRequestSchema,
	withdrawResponseSchema,
} from './applications.js';
import {
	adminErrorsQuerySchema,
	adminErrorsResponseSchema,
	adminMeSchema,
	adminReportActionRequestSchema,
	adminReportActionResponseSchema,
	adminReportParamsSchema,
	adminReportsQuerySchema,
	adminReportsResponseSchema,
	adminLoginCodeRequestSchema,
	adminLoginCodeResponseSchema,
	adminLoginVerifyRequestSchema,
	adminSignOutResponseSchema,
	adminUserActionRequestSchema,
	adminUserActionResponseSchema,
	adminUserCardSchema,
	adminUserFlagsRequestSchema,
	adminUserNoteRequestSchema,
	adminUserNoteResponseSchema,
	adminUserParamsSchema,
	adminUserRoleRequestSchema,
	adminUsersQuerySchema,
	adminUsersResponseSchema,
} from './admin.js';
import { clientErrorRequestSchema, clientErrorResponseSchema } from './client-errors.js';
import { consentRequestSchema } from './consent.js';
import {
	markReadRequestSchema,
	markReadResponseSchema,
	messagesQuerySchema,
	myChatsResponseSchema,
	messagesResponseSchema,
	sendMessageRequestSchema,
	sendMessageResponseSchema,
} from './chat.js';
import {
	blockParamsSchema,
	blockResponseSchema,
	blockedUsersResponseSchema,
	createReportRequestSchema,
	reportResponseSchema,
} from './safety.js';
import {
	citiesResponseSchema,
	interestsResponseSchema,
	profileSchema,
	setInterestsRequestSchema,
	userIdParamsSchema,
} from './profile.js';
import {
	eventReviewsSchema,
	submitReviewsRequestSchema,
	userReviewsQuerySchema,
	userReviewsResponseSchema,
} from './reviews.js';
import {
	markNotificationsReadResponseSchema,
	notificationSettingsViewSchema,
	notificationsQuerySchema,
	notificationsResponseSchema,
	updateNotificationSettingsRequestSchema,
} from './notifications.js';
import {
	deleteMeResponseSchema,
	meSchema,
	telegramAuthRequestSchema,
	telegramAuthResponseSchema,
	updateMeRequestSchema,
} from './auth.js';
import {
	cancelEventRequestSchema,
	createEventRequestSchema,
	recruitmentRequestSchema,
	updateEventRequestSchema,
	placePreviewRequestSchema,
	placePreviewResponseSchema,
} from './event-create.js';
import {
	eventDetailSchema,
	eventIdParamsSchema,
	eventsListQuerySchema,
	eventsListResponseSchema,
	eventStatusQuerySchema,
	eventStatusResponseSchema,
	eventsMapQuerySchema,
	eventsMapResponseSchema,
	meEventsQuerySchema,
	saveEventResponseSchema,
	updateEventResponseSchema,
} from './events.js';
import {
	eventApplicationsSchema,
	noShowRequestSchema,
	participantParamsSchema,
} from './organizer-applications.js';

/** Route contract (D28): the web's typed fetch client and the API share these definitions. */
export interface RouteContract {
	method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
	/** Fastify-style path; `:name` segments are filled from `params`. */
	path: string;
	body: z.ZodType | undefined;
	query: z.ZodType | undefined;
	params: z.ZodType | undefined;
	response: z.ZodType;
	/** False for routes that must not send/refresh the access token (auth itself). */
	auth: boolean;
}

export const routes = {
	telegramAuth: {
		method: 'POST',
		path: '/v1/auth/telegram',
		body: telegramAuthRequestSchema,
		query: undefined,
		params: undefined,
		response: telegramAuthResponseSchema,
		auth: false,
	},
	listEvents: {
		method: 'GET',
		path: '/v1/events',
		body: undefined,
		query: eventsListQuerySchema,
		params: undefined,
		response: eventsListResponseSchema,
		auth: true,
	},
	mapEvents: {
		method: 'GET',
		path: '/v1/events/map',
		body: undefined,
		query: eventsMapQuerySchema,
		params: undefined,
		response: eventsMapResponseSchema,
		auth: true,
	},
	eventStatuses: {
		method: 'GET',
		path: '/v1/events/status',
		body: undefined,
		query: eventStatusQuerySchema,
		params: undefined,
		response: eventStatusResponseSchema,
		auth: true,
	},
	createEvent: {
		method: 'POST',
		path: '/v1/events',
		body: createEventRequestSchema,
		query: undefined,
		params: undefined,
		response: eventDetailSchema,
		auth: true,
	},
	placePreview: {
		method: 'POST',
		path: '/v1/events/place-preview',
		body: placePreviewRequestSchema,
		query: undefined,
		params: undefined,
		response: placePreviewResponseSchema,
		auth: true,
	},
	getEvent: {
		method: 'GET',
		path: '/v1/events/:id',
		body: undefined,
		query: undefined,
		params: eventIdParamsSchema,
		response: eventDetailSchema,
		auth: true,
	},
	updateMe: {
		method: 'PATCH',
		path: '/v1/me',
		body: updateMeRequestSchema,
		query: undefined,
		params: undefined,
		response: meSchema,
		auth: true,
	},
	consent: {
		method: 'POST',
		path: '/v1/me/consent',
		body: consentRequestSchema,
		query: undefined,
		params: undefined,
		response: meSchema,
		auth: true,
	},
	myNotifications: {
		method: 'GET',
		path: '/v1/me/notifications',
		body: undefined,
		query: notificationsQuerySchema,
		params: undefined,
		response: notificationsResponseSchema,
		auth: true,
	},
	markNotificationsRead: {
		method: 'POST',
		path: '/v1/me/notifications/read',
		body: undefined,
		query: undefined,
		params: undefined,
		response: markNotificationsReadResponseSchema,
		auth: true,
	},
	notificationSettings: {
		method: 'GET',
		path: '/v1/me/notification-settings',
		body: undefined,
		query: undefined,
		params: undefined,
		response: notificationSettingsViewSchema,
		auth: true,
	},
	updateNotificationSettings: {
		method: 'PUT',
		path: '/v1/me/notification-settings',
		body: updateNotificationSettingsRequestSchema,
		query: undefined,
		params: undefined,
		response: notificationSettingsViewSchema,
		auth: true,
	},
	deleteMe: {
		method: 'DELETE',
		path: '/v1/me',
		body: undefined,
		query: undefined,
		params: undefined,
		response: deleteMeResponseSchema,
		auth: true,
	},
	myEvents: {
		method: 'GET',
		path: '/v1/me/events',
		body: undefined,
		query: meEventsQuerySchema,
		params: undefined,
		response: eventsListResponseSchema,
		auth: true,
	},
	updateEvent: {
		method: 'PATCH',
		path: '/v1/events/:id',
		body: updateEventRequestSchema,
		query: undefined,
		params: eventIdParamsSchema,
		response: updateEventResponseSchema,
		auth: true,
	},
	cancelEvent: {
		method: 'POST',
		path: '/v1/events/:id/cancel',
		body: cancelEventRequestSchema,
		query: undefined,
		params: eventIdParamsSchema,
		response: eventDetailSchema,
		auth: true,
	},
	setRecruitment: {
		method: 'POST',
		path: '/v1/events/:id/recruitment',
		body: recruitmentRequestSchema,
		query: undefined,
		params: eventIdParamsSchema,
		response: eventDetailSchema,
		auth: true,
	},
	applyToEvent: {
		method: 'POST',
		path: '/v1/events/:id/applications',
		body: applyRequestSchema,
		query: undefined,
		params: eventIdParamsSchema,
		response: applyResponseSchema,
		auth: true,
	},
	withdrawApplication: {
		method: 'DELETE',
		path: '/v1/applications/:id',
		body: undefined,
		query: undefined,
		params: applicationIdParamsSchema,
		response: withdrawResponseSchema,
		auth: true,
	},
	eventApplications: {
		method: 'GET',
		path: '/v1/events/:id/applications',
		body: undefined,
		query: undefined,
		params: eventIdParamsSchema,
		response: eventApplicationsSchema,
		auth: true,
	},
	acceptApplication: {
		method: 'POST',
		path: '/v1/applications/:id/accept',
		body: undefined,
		query: undefined,
		params: applicationIdParamsSchema,
		response: eventApplicationsSchema,
		auth: true,
	},
	rejectApplication: {
		method: 'POST',
		path: '/v1/applications/:id/reject',
		body: undefined,
		query: undefined,
		params: applicationIdParamsSchema,
		response: eventApplicationsSchema,
		auth: true,
	},
	removeParticipant: {
		method: 'POST',
		path: '/v1/events/:id/participants/:userId/remove',
		body: undefined,
		query: undefined,
		params: participantParamsSchema,
		response: eventApplicationsSchema,
		auth: true,
	},
	markNoShow: {
		method: 'POST',
		path: '/v1/events/:id/participants/:userId/no-show',
		body: noShowRequestSchema,
		query: undefined,
		params: participantParamsSchema,
		response: eventApplicationsSchema,
		auth: true,
	},
	eventMessages: {
		method: 'GET',
		path: '/v1/events/:id/messages',
		body: undefined,
		query: messagesQuerySchema,
		params: eventIdParamsSchema,
		response: messagesResponseSchema,
		auth: true,
	},
	sendMessage: {
		method: 'POST',
		path: '/v1/events/:id/messages',
		body: sendMessageRequestSchema,
		query: undefined,
		params: eventIdParamsSchema,
		response: sendMessageResponseSchema,
		auth: true,
	},
	markChatRead: {
		method: 'POST',
		path: '/v1/events/:id/read',
		body: markReadRequestSchema,
		query: undefined,
		params: eventIdParamsSchema,
		response: markReadResponseSchema,
		auth: true,
	},
	myChats: {
		method: 'GET',
		path: '/v1/me/chats',
		body: undefined,
		query: undefined,
		params: undefined,
		response: myChatsResponseSchema,
		auth: true,
	},
	userReviews: {
		method: 'GET',
		path: '/v1/users/:id/reviews',
		body: undefined,
		query: userReviewsQuerySchema,
		params: userIdParamsSchema,
		response: userReviewsResponseSchema,
		auth: true,
	},
	myEventReviews: {
		method: 'GET',
		path: '/v1/events/:id/reviews/mine',
		body: undefined,
		query: undefined,
		params: eventIdParamsSchema,
		response: eventReviewsSchema,
		auth: true,
	},
	submitReviews: {
		method: 'POST',
		path: '/v1/events/:id/reviews',
		body: submitReviewsRequestSchema,
		query: undefined,
		params: eventIdParamsSchema,
		response: eventReviewsSchema,
		auth: true,
	},
	userProfile: {
		method: 'GET',
		path: '/v1/users/:id',
		body: undefined,
		query: undefined,
		params: userIdParamsSchema,
		response: profileSchema,
		auth: true,
	},
	setInterests: {
		method: 'PUT',
		path: '/v1/me/interests',
		body: setInterestsRequestSchema,
		query: undefined,
		params: undefined,
		response: profileSchema,
		auth: true,
	},
	deletePhoto: {
		method: 'DELETE',
		path: '/v1/me/photo',
		body: undefined,
		query: undefined,
		params: undefined,
		response: profileSchema,
		auth: true,
	},
	interests: {
		method: 'GET',
		path: '/v1/interests',
		body: undefined,
		query: undefined,
		params: undefined,
		response: interestsResponseSchema,
		auth: true,
	},
	cities: {
		method: 'GET',
		path: '/v1/cities',
		body: undefined,
		query: undefined,
		params: undefined,
		response: citiesResponseSchema,
		auth: true,
	},
	createReport: {
		method: 'POST',
		path: '/v1/reports',
		body: createReportRequestSchema,
		query: undefined,
		params: undefined,
		response: reportResponseSchema,
		auth: true,
	},
	blockUser: {
		method: 'PUT',
		path: '/v1/blocks/:userId',
		body: undefined,
		query: undefined,
		params: blockParamsSchema,
		response: blockResponseSchema,
		auth: true,
	},
	unblockUser: {
		method: 'DELETE',
		path: '/v1/blocks/:userId',
		body: undefined,
		query: undefined,
		params: blockParamsSchema,
		response: blockResponseSchema,
		auth: true,
	},
	myBlocks: {
		method: 'GET',
		path: '/v1/me/blocks',
		body: undefined,
		query: undefined,
		params: undefined,
		response: blockedUsersResponseSchema,
		auth: true,
	},
	botAccess: {
		method: 'POST',
		path: '/v1/me/bot-access',
		body: botAccessRequestSchema,
		query: undefined,
		params: undefined,
		response: meSchema,
		auth: true,
	},
	saveEvent: {
		method: 'PUT',
		path: '/v1/events/:id/save',
		body: undefined,
		query: undefined,
		params: eventIdParamsSchema,
		response: saveEventResponseSchema,
		auth: true,
	},
	unsaveEvent: {
		method: 'DELETE',
		path: '/v1/events/:id/save',
		body: undefined,
		query: undefined,
		params: eventIdParamsSchema,
		response: saveEventResponseSchema,
		auth: true,
	},
	clientError: {
		method: 'POST',
		path: '/v1/client-errors',
		body: clientErrorRequestSchema,
		query: undefined,
		params: undefined,
		response: clientErrorResponseSchema,
		auth: true,
	},
	// Admin panel (T-1102a, D46): cookie session, never the Mini App bearer token.
	adminLoginCode: {
		method: 'POST',
		path: '/v1/admin/login/code',
		body: adminLoginCodeRequestSchema,
		query: undefined,
		params: undefined,
		response: adminLoginCodeResponseSchema,
		auth: false,
	},
	adminLoginVerify: {
		method: 'POST',
		path: '/v1/admin/login/verify',
		body: adminLoginVerifyRequestSchema,
		query: undefined,
		params: undefined,
		response: adminMeSchema,
		auth: false,
	},
	adminMe: {
		method: 'GET',
		path: '/v1/admin/me',
		body: undefined,
		query: undefined,
		params: undefined,
		response: adminMeSchema,
		auth: false,
	},
	adminSignOut: {
		method: 'DELETE',
		path: '/v1/admin/session',
		body: undefined,
		query: undefined,
		params: undefined,
		response: adminSignOutResponseSchema,
		auth: false,
	},
	adminReports: {
		method: 'GET',
		path: '/v1/admin/reports',
		body: undefined,
		query: adminReportsQuerySchema,
		params: undefined,
		response: adminReportsResponseSchema,
		auth: false,
	},
	adminReportAction: {
		method: 'POST',
		path: '/v1/admin/reports/:reportId/actions',
		body: adminReportActionRequestSchema,
		query: undefined,
		params: adminReportParamsSchema,
		response: adminReportActionResponseSchema,
		auth: false,
	},
	adminUsers: {
		method: 'GET',
		path: '/v1/admin/users',
		body: undefined,
		query: adminUsersQuerySchema,
		params: undefined,
		response: adminUsersResponseSchema,
		auth: false,
	},
	adminUserCard: {
		method: 'GET',
		path: '/v1/admin/users/:userId',
		body: undefined,
		query: undefined,
		params: adminUserParamsSchema,
		response: adminUserCardSchema,
		auth: false,
	},
	adminUserNote: {
		method: 'POST',
		path: '/v1/admin/users/:userId/notes',
		body: adminUserNoteRequestSchema,
		query: undefined,
		params: adminUserParamsSchema,
		response: adminUserNoteResponseSchema,
		auth: false,
	},
	adminUserFlags: {
		method: 'PUT',
		path: '/v1/admin/users/:userId/flags',
		body: adminUserFlagsRequestSchema,
		query: undefined,
		params: adminUserParamsSchema,
		response: adminUserActionResponseSchema,
		auth: false,
	},
	adminUserRole: {
		method: 'PUT',
		path: '/v1/admin/users/:userId/role',
		body: adminUserRoleRequestSchema,
		query: undefined,
		params: adminUserParamsSchema,
		response: adminUserActionResponseSchema,
		auth: false,
	},
	adminUserAction: {
		method: 'POST',
		path: '/v1/admin/users/:userId/actions',
		body: adminUserActionRequestSchema,
		query: undefined,
		params: adminUserParamsSchema,
		response: adminUserActionResponseSchema,
		auth: false,
	},
	adminErrors: {
		method: 'GET',
		path: '/v1/admin/errors',
		body: undefined,
		query: adminErrorsQuerySchema,
		params: undefined,
		response: adminErrorsResponseSchema,
		auth: false,
	},
} as const satisfies Record<string, RouteContract>;
